import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    Breadcrumb, Card, Descriptions, Typography, Spin, Tag, Table, Space, Button,
    Modal, message, Form, Input, InputNumber, Select, Upload, Grid
} from 'antd';
import {
    RightOutlined, EditOutlined, ReloadOutlined, PlusOutlined,
    DeleteOutlined, UploadOutlined, SearchOutlined
} from '@ant-design/icons';
import { acheteTerrainService } from '../../common/services';
// import acheteTerrainService from '../../common/services/acheteTerrainService';

const { Title, Text } = Typography;
const { Option } = Select;

const statusColor = (s) => {
    if (s === 'disponible') return 'green';
    if (s === 'reserve' || s === 'réservé' || s === 'reserver') return 'gold';
    if (s === 'vendu') return 'red';
    return 'default';
};

const currency = (n) =>
    typeof n === 'number'
        ? `${n.toLocaleString('fr-FR')} FCFA`
        : n
            ? `${Number(n).toLocaleString('fr-FR')} FCFA`
            : '—';

const DetailSite = () => {
    const { id } = useParams();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    // ---- data ----
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);

    // lots
    const [lots, setLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(false);
    const [qLots, setQLots] = useState('');

    // modals
    const [editSiteOpen, setEditSiteOpen] = useState(false);
    const [lotModalOpen, setLotModalOpen] = useState(false);
    const [editingLot, setEditingLot] = useState(null);

    const [formSite] = Form.useForm();
    const [formLot] = Form.useForm();

    const filteredLots = useMemo(() => {
        const q = (qLots || '').trim().toLowerCase();
        if (!q) return lots;
        return (lots || []).filter((l) =>
            [l.intitule, l?.remarque, String(l?.superficieM2), String(l?.prixTotal)]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [qLots, lots]);

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const s = await acheteTerrainService.getSiteById(id);
            setSite(s);
            setLots(s?.lots || []);
        } catch (e) {
            console.error(e);
            message.error("Impossible de charger le site.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const openEditSite = () => {
        formSite.setFieldsValue({
            nom: site?.nom,
            localisation: site?.localisation,
            estViabilise: site?.estViabilise ? 'true' : 'false',
        });
        setEditSiteOpen(true);
    };

    const submitEditSite = async () => {
        try {
            const values = await formSite.validateFields();
            const payload = {
                nom: values.nom?.trim(),
                localisation: values.localisation?.trim(),
                estViabilise: values.estViabilise === 'true',
            };
            const updated = await acheteTerrainService.updateSite(site._id, payload);
            setSite(updated?.site || updated);
            message.success("Site mis à jour.");
            setEditSiteOpen(false);
        } catch (e) {
            if (e?.errorFields) return; // validation antd
            console.error(e);
            message.error("Échec de la mise à jour.");
        }
    };

    const recompute = async () => {
        try {
            await acheteTerrainService.recomputeNombreLots(site._id);
            message.success("nombreTotalLots recalculé.");
            load();
        } catch (e) {
            console.error(e);
            message.error("Échec du recalcul.");
        }
    };

    const openNewLot = () => {
        setEditingLot(null);
        formLot.resetFields();
        setLotModalOpen(true);
    };

    const openEditLot = (lot) => {
        setEditingLot(lot);
        formLot.setFieldsValue({
            intitule: lot.intitule,
            superficieM2: lot.superficieM2,
            prixTotal: lot.prixTotal,
            remarque: lot.remarque || '',
            status: lot.status || 'disponible',
        });
        setLotModalOpen(true);
    };

    const submitLot = async () => {
        try {
            const values = await formLot.validateFields();
            const payload = {
                intitule: values.intitule?.trim(),
                superficieM2: Number(values.superficieM2),
                prixTotal: Number(values.prixTotal),
                remarque: values.remarque?.trim() || undefined,
                status: values.status || undefined,
            };

            if (!editingLot) {
                // create
                const res = await acheteTerrainService.addLot(site._id, payload);
                setSite(res?.site || res);
                setLots(res?.site?.lots || res?.lots || []);
                message.success("Lot ajouté.");
            } else {
                // update
                const res = await acheteTerrainService.updateLot(site._id, editingLot._id, payload);
                // le contrôleur renvoie { site, lot }
                const updatedSite = res?.site || site;
                setSite(updatedSite);
                setLots(updatedSite?.lots || []);
                message.success("Lot mis à jour.");
            }

            setLotModalOpen(false);
            setEditingLot(null);
        } catch (e) {
            if (e?.errorFields) return; // validation antd
            console.error(e);
            message.error("Échec de l’enregistrement du lot.");
        }
    };

    const deleteLot = (lot) => {
        Modal.confirm({
            title: "Supprimer ce lot ?",
            content: (
                <>
                    <p><strong>{lot.intitule}</strong> — {currency(lot.prixTotal)}</p>
                    <p>Si le lot est « vendu », utilisez la suppression forcée.</p>
                </>
            ),
            okText: 'Supprimer',
            cancelText: 'Annuler',
            okType: 'danger',
            centered: true,
            onOk: async () => {
                try {
                    await acheteTerrainService.deleteLot(site._id, lot._id);
                    message.success("Lot supprimé.");
                    load();
                } catch (e) {
                    const needForce = e?.status === 409 || String(e?.message || '').includes('vendu');
                    if (needForce) {
                        Modal.confirm({
                            title: "Suppression forcée ?",
                            content: "Ce lot est vendu. Confirmez la suppression forcée.",
                            okText: "Forcer",
                            okType: "danger",
                            cancelText: "Annuler",
                            centered: true,
                            onOk: async () => {
                                try {
                                    await acheteTerrainService.deleteLot(site._id, lot._id, { force: true });
                                    message.success("Lot supprimé (forcé).");
                                    load();
                                } catch (err2) {
                                    console.error(err2);
                                    message.error("Échec de la suppression forcée.");
                                }
                            }
                        });
                    } else {
                        console.error(e);
                        message.error("Échec de la suppression.");
                    }
                }
            }
        });
    };

    const importJson = async (file) => {
        try {
            const text = await file.text();
            const json = JSON.parse(text);
            if (!Array.isArray(json)) throw new Error('Le fichier doit contenir un tableau JSON de lots.');
            const res = await acheteTerrainService.importLotsJson(site._id, json);
            message.success(`Import effectué (${res?.inserted ?? json.length} insérés).`);
            load();
        } catch (e) {
            console.error(e);
            message.error("Échec de l’import JSON.");
        }
        return false; // empêcher l’upload réel
    };

    const columnsLots = [
        { title: 'Intitulé', dataIndex: 'intitule', key: 'intitule' },
        {
            title: 'Superficie',
            dataIndex: 'superficieM2',
            key: 'superficieM2',
            render: (v) => v ? `${Number(v).toLocaleString('fr-FR')} m²` : '—',
            width: 140
        },
        {
            title: 'Prix total',
            dataIndex: 'prixTotal',
            key: 'prixTotal',
            render: (v) => currency(v),
            width: 160
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (s) => <Tag color={statusColor(s)}>{(s || 'disponible')}</Tag>,
            width: 130
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, r) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => openEditLot(r)} />
                    <Button danger icon={<DeleteOutlined />} size="small" onClick={() => deleteLot(r)} />
                </Space>
            )
        }
    ];

    if (loading || !site) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Spin tip="Chargement du site..." size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: isMobile ? 12 : 24 }}>
            <Breadcrumb
                separator={<RightOutlined style={{ fontSize: isMobile ? 14 : 16, color: '#999', fontWeight: 'bold' }} />}
                style={{ marginBottom: isMobile ? 12 : 16 }}
            >
                <Breadcrumb.Item>
                    <Link to="/dashboard" style={{ fontWeight: 'bold' }}>🏠 ACCUEIL</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/dashboard/sites" style={{ fontWeight: 'bold' }}>🧱 SITES</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <span style={{ fontWeight: 'bold' }}>🔍 DÉTAIL</span>
                </Breadcrumb.Item>
            </Breadcrumb>

            <Card bordered size={isMobile ? 'small' : 'default'}>
                <Title level={isMobile ? 4 : 3} style={{ marginBottom: isMobile ? 12 : 16 }}>
                    {site.nom} <Text type="secondary">— {site.localisation}</Text>
                </Title>

                <Space wrap style={{ marginBottom: 16 }}>
                    <Button icon={<EditOutlined />} onClick={openEditSite} size={controlSize}>
                        Éditer le site
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={recompute} size={controlSize}>
                        Recalculer “nombreTotalLots”
                    </Button>
                    <Upload beforeUpload={importJson} showUploadList={false} accept=".json">
                        <Button icon={<UploadOutlined />} size={controlSize}>
                            Importer lots (.json)
                        </Button>
                    </Upload>
                </Space>

                <Descriptions
                    bordered
                    size={controlSize}
                    column={isMobile ? 1 : 2}
                    labelStyle={{ width: isMobile ? 140 : 220 }}
                >
                    <Descriptions.Item label="Nom">{site.nom}</Descriptions.Item>
                    <Descriptions.Item label="Localisation">{site.localisation}</Descriptions.Item>
                    <Descriptions.Item label="Viabilisé">
                        {site.estViabilise ? <Tag color="green">Oui</Tag> : <Tag>Non</Tag>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Nombre de lots">
                        {site?.nombreTotalLots ?? (site?.lots?.length || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Créé le">
                        {site.createdAt ? dayjs(site.createdAt).format('DD/MM/YYYY HH:mm') : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Mis à jour le">
                        {site.updatedAt ? dayjs(site.updatedAt).format('DD/MM/YYYY HH:mm') : '—'}
                    </Descriptions.Item>
                </Descriptions>

                {/* Header lots */}
                <div style={{ marginTop: 20, marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Lots</Title>
                    <Space style={{ marginLeft: 'auto' }} wrap>
                        <Input
                            placeholder="Rechercher un lot…"
                            allowClear
                            prefix={<SearchOutlined />}
                            size={controlSize}
                            onChange={(e) => setQLots(e.target.value)}
                            style={{ width: 260 }}
                        />
                        <Button type="primary" icon={<PlusOutlined />} onClick={openNewLot} size={controlSize}>
                            Nouveau lot
                        </Button>
                    </Space>
                </div>

                <Table
                    size={controlSize}
                    rowKey="_id"
                    loading={lotsLoading}
                    columns={columnsLots}
                    dataSource={filteredLots}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    scroll={{ x: true, y: isMobile ? 360 : undefined }}
                    sticky
                />
            </Card>

            {/* Modal Éditer site */}
            <Modal
                title="Éditer le site"
                open={editSiteOpen}
                onCancel={() => setEditSiteOpen(false)}
                onOk={submitEditSite}
                okText="Enregistrer"
                cancelText="Annuler"
                destroyOnClose
            >
                <Form layout="vertical" form={formSite}>
                    <Form.Item label="Nom" name="nom" rules={[{ required: true, message: 'Nom requis' }]}>
                        <Input placeholder="Nom du site" />
                    </Form.Item>
                    <Form.Item label="Localisation" name="localisation" rules={[{ required: true, message: 'Localisation requise' }]}>
                        <Input placeholder="Localisation" />
                    </Form.Item>
                    <Form.Item label="Viabilisé" name="estViabilise" rules={[{ required: true }]}>
                        <Select placeholder="Sélectionner">
                            <Option value="true">Oui</Option>
                            <Option value="false">Non</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Lot (create/update) */}
            <Modal
                title={editingLot ? 'Modifier le lot' : 'Nouveau lot'}
                open={lotModalOpen}
                onCancel={() => { setLotModalOpen(false); setEditingLot(null); }}
                onOk={submitLot}
                okText="Enregistrer"
                cancelText="Annuler"
                destroyOnClose
            >
                <Form layout="vertical" form={formLot}>
                    <Form.Item label="Intitulé" name="intitule" rules={[{ required: true, message: 'Intitulé requis' }]}>
                        <Input placeholder="Ex: Lot A2" />
                    </Form.Item>
                    <Form.Item label="Superficie (m²)" name="superficieM2" rules={[{ required: true, message: 'Superficie requise' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Prix total (FCFA)" name="prixTotal" rules={[{ required: true, message: 'Prix requis' }]}>
                        <InputNumber min={1} step={1000} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Remarque" name="remarque">
                        <Input.TextArea rows={3} placeholder="Optionnel" />
                    </Form.Item>
                    <Form.Item label="Statut" name="status" initialValue="disponible">
                        <Select>
                            <Option value="disponible">Disponible</Option>
                            <Option value="reserve">Réservé</Option>
                            <Option value="vendu">Vendu</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DetailSite;
